import React from "react";

/**
 * Returns Israel timezone offset for a given date (DST-aware).
 * Israel: UTC+2 (winter), UTC+3 (summer, ~March 28 – Oct 26).
 */
function getIsraelOffset(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const march28 = new Date(y, 2, 28);
  const oct26 = new Date(y, 9, 26);
  const eventDate = new Date(y, m - 1, d);
  const isSummer = eventDate >= march28 && eventDate < oct26;
  return isSummer ? "+03:00" : "+02:00";
}

function pad(num) {
  return String(num).padStart(2, "0");
}

function escapeIcs(value) {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
}

function toUtcIcs(date) {
  return (
    date.getUTCFullYear() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    "T" +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    "Z"
  );
}

function toIsraelLocalStamp(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}${get("month")}${get("day")}T${get("hour")}${get("minute")}${get("second")}`;
}

function getCalendarStrategy() {
  if (typeof navigator === "undefined") return "ics-download";

  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isIOSSafari =
    isIOS &&
    /WebKit/i.test(ua) &&
    !/CriOS|FxiOS|OPiOS|EdgiOS|FBAN|FBAV|Instagram|Line|WhatsApp/i.test(ua);

  if (isAndroid || (isIOS && !isIOSSafari)) return "google";
  if (isIOSSafari) return "ios-ics";
  return "ics-download";
}

function buildIcs({ title, description, location, start, end }) {
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CallUp//Invitation//HE",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${start.getTime()}@callup.app`,
    `DTSTAMP:${toUtcIcs(new Date())}`,
    `DTSTART:${toUtcIcs(start)}`,
    `DTEND:${toUtcIcs(end)}`,
    `SUMMARY:${escapeIcs(title)}`,
    `DESCRIPTION:${escapeIcs(description)}`,
    `LOCATION:${escapeIcs(location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

function buildGoogleUrl({ title, description, location, start, end }) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${toIsraelLocalStamp(start)}/${toIsraelLocalStamp(end)}`,
    details: description || "",
    location: location || "",
    ctz: "Asia/Jerusalem",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function downloadIcs(ics, filename) {
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

const Cal = (props) => {
  const handleAddToCalendar = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!props.data?.date || !props.data?.hour1) return;

    const date = props.data.date.split("T")[0];
    const hourParts = props.data.hour1.split(":");
    const hour = (hourParts[0] || "00").padStart(2, "0");
    const minute = (hourParts[1] || "00").padStart(2, "0");
    const offset = getIsraelOffset(date);
    const start = new Date(`${date}T${hour}:${minute}:00${offset}`);
    const durationHours = props.data.eventType ? 5 : 3;
    const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);

    const title = props.data.eventType
      ? `${props.data.brideFN} ו${props.data.groomFN} - החתונה!`
      : props.data.calendarName !== ""
        ? props.data.calendarName
        : "אירוע";
    const description = `"${props.data.hall || ""}", ${props.data.waze || ""}`;
    const location = [props.data.address, props.data.city]
      .filter(Boolean)
      .join(", ");
    const event = { title, description, location, start, end };
    const strategy = getCalendarStrategy();

    if (strategy === "google") {
      window.open(buildGoogleUrl(event), "_blank", "noopener,noreferrer");
      return;
    }

    const ics = buildIcs(event);
    if (strategy === "ios-ics") {
      window.location.href =
        "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
      return;
    }

    downloadIcs(ics, "event.ics");
  };

  return (
    <a
      href="#add-to-calendar"
      onClick={handleAddToCalendar}
      style={{ color: "inherit", textDecoration: "none" }}
    >
      {props.div}
    </a>
  );
};

export default Cal;
